import React from "react";
import { Helmet } from "react-helmet";
import { Property } from "../../types/property";
import { monthlyRentPcm } from "../../util/propertyParsing";

interface PropertySeoProps {
  property: Property;
  imageUrl?: string;
}

function getSiteOrigin(): string {
  if (typeof window !== "undefined" && window.location.origin) {
    return window.location.origin;
  }
  return "https://lettingengine.com";
}

function stripHtml(text: string): string {
  return text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

const PropertySeo: React.FC<PropertySeoProps> = ({ property, imageUrl }) => {
  const origin = getSiteOrigin();
  const url = `${origin}/services/rent/properties/${property.id}`;
  const pcm = monthlyRentPcm(property.price, property.rent_frequency);
  const title = `${property.display_address} | Letting Engine`;
  const description = stripHtml(
    property.summary ||
      `${property.bedrooms ?? ""} bed ${property.property_type} to rent in ${property.display_address}. £${pcm.toLocaleString()} PCM.`
  ).slice(0, 160);
  const ogImage =
    imageUrl || "https://via.placeholder.com/1200x630?text=Letting+Engine";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: property.display_address,
    description: stripHtml(property.summary || description),
    url,
    datePosted: property.create_date || property.update_date,
    image: ogImage,
    address: {
      "@type": "PostalAddress",
      streetAddress: property.display_address,
      addressLocality: property.address_3 || property.address_4 || "London",
      postalCode: property.postcode,
      addressCountry: "GB",
    },
    offers: {
      "@type": "Offer",
      price: pcm,
      priceCurrency: "GBP",
      availability:
        property.status?.toLowerCase() === "let agreed"
          ? "https://schema.org/SoldOut"
          : "https://schema.org/InStock",
    },
    numberOfRooms: property.bedrooms ?? undefined,
    floorSize: property.internal_area
      ? {
          "@type": "QuantitativeValue",
          value: property.internal_area,
          unitCode: "FTK",
        }
      : undefined,
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default PropertySeo;
